import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import styles from './styles.module.css';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { AlertMessagesContext } from 'react-alert-messages';
import { useParams, useHistory } from 'react-router-dom';
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
  Autocomplete,
  InputAdornment,
  IconButton as MuiIconButton,
} from '@mui/material';
import { Button, TextField, MenuItem, IconButton, Collapse, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { CartHelper } from 'hooks/CartHelper';
import { InventoryContext } from 'Context/InventoryContext';
import { ShopContext } from 'Context/ShopContext';
import { WarehouseContext } from 'Context/WarehouseContext';
import ShopsHelper from 'helpers/ShopsHelper';
import TerminalService from 'services/TerminalServices';
import MessagePopup from 'components/utils/Alert/MessagePopup';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import StockTransferService from 'services/StockTransferService';
import { StockTransferHelper } from 'hooks/StockTransferHelper';
import { ROUTES } from 'const';
import BatchSelectionPopup from 'components/popups/BatchSelectionPopup';
import ItemService from 'services/ItemService';

const TRANSFER_STATUS = {
  DRAFT: 'DRAFT',
  TRANSIT: 'TRANSIT',
  RECEIVED: 'RECEIVED',
  REJECTED: 'REJECTED',
};

let initialFetchPromise = null;
let unmountTimeout = null;

function NewStockTransfer() {
  const history = useHistory();
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { t: tPopup } = useTranslation('popup');

  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { products, getProducts, stockBatches, getStockBatches } = useContext(InventoryContext);
  const { warehouses, getWarehouses, warehouseProducts, getWarehouseProducts } = useContext(WarehouseContext);

  const { id } = useParams();
  const { shop } = useContext(ShopContext);
  const CartManager = CartHelper();
  const StockTransferManager = StockTransferHelper();

  const [transferItems, setTransferItems] = useState([]);
  const [itemName, setItemName] = useState('');
  const [rate, setRate] = useState('');
  const [stock, setStock] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [tax, setTax] = useState(0);
  const [cess, setCess] = useState(0);
  const [item, setItem] = useState(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [editData, setEditData] = useState({});
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [transferId, setTransferId] = useState(id ?? null);
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [disabledTerminal, setDisabledTerminal] = useState(null);
  const [remark, setRemark] = useState('');
  const [profitPercentage, setProfitPercentage] = useState(0);
  const [chainShops, setChainShops] = useState([]);
  const [selectedReceivingShop, setSelectedReceivingShop] = useState(null);
  const [selectedItemBatches, setSelectedItemBatches] = useState([]);
  const [batchPopupAnchor, setBatchPopupAnchor] = useState(null);
  const [localBatches, setLocalBatches] = useState([]);
  const [editingTableItemIndex, setEditingTableItemIndex] = useState(null);
  const [originalBatchQtys, setOriginalBatchQtys] = useState({});
  const [subItemBatchPopup, setSubItemBatchPopup] = useState({
    open: false,
    anchorEl: null,
    item: null,
    subItem: null,
    saleItemIndex: null,
  });
  const [warehouseId, setWarehouseId] = useState(null);
  const [batchName, setBatchName] = useState(ShopsHelper.generateBatchName());
  const [rateCalculationMode, setRateCalculationMode] = useState('cost');

  const hasBatchEnabledItems = transferItems.some((i) => i.item?.is_batch_enabled);

  const filteredProductsList = useMemo(() => {
    return products || [];
  }, [products]);

  const filteredStockBatches = useMemo(() => {
    const currentWhId = warehouseId === 0 ? null : warehouseId;
    if (!currentWhId) return localBatches || [];
    return localBatches.filter((b) => {
      const bWhId = b.warehouse?.id || b.warehouse || null;
      return String(bWhId) === String(currentWhId);
    });
  }, [warehouseId, localBatches]);

  const dltItem = (item) => {
    setTransferItems(transferItems.filter(({ item_id, rate }) => item_id !== item.item_id || rate !== item.rate));
  };

  const resetItemFields = () => {
    setItemName('');
    setRate('');
    setQuantity('');
    setTax('');
    setCess('');
    setTotalAmount('');
    setItem(null);
    setSelectedItemBatches([]);
    setBatchPopupAnchor(null);
  };

  const clearSaleHandler = () => {
    setRate('');
    resetItemFields();
    setTransferItems([]);
    setEditData({});
    setRemark('');
    setTransferId(null);
    setSelectedReceivingShop(null);
    setProfitPercentage(0);
    setRateCalculationMode('cost');
    setOriginalBatchQtys({});
    setBatchName(ShopsHelper.generateBatchName());
  };

  const addItem = () => {
    let sub_item = [];
    if (item && item.type === 'COMBO') {
      sub_item = (item.details?.sub_items || []).map((subItem) => {
        const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
        const requiredQty = subItem.fraction * quantity;

        let selectedBatches = [];
        if (prod?.is_batch_enabled) {
          // Auto-allocate batches
          const availableBatches = (stockBatches || [])
            .filter((b) => {
              const isSameItem = String(b.item) === String(subItem.item_id);
              if (!isSameItem) return false;

              // Expiry check: treat 'Z' as local time for accurate comparison
              const expiryDate = b.expiry_date ? new Date(b.expiry_date.replace('Z', '')) : null;
              const isExpired = expiryDate && expiryDate <= new Date();
              return !isExpired;
            })
            .sort((a, b) => {
              if (!a.expiry_date) return 1;
              if (!b.expiry_date) return -1;
              return new Date(a.expiry_date) - new Date(b.expiry_date);
            });

          let remainingToAllocate = requiredQty;
          for (const batch of availableBatches) {
            if (remainingToAllocate <= 0) break;
            const take = Math.min(remainingToAllocate, batch.stock || 0);
            if (take > 0) {
              selectedBatches.push({
                batch_id: batch.id,
                quantity: take,
                batch_no: batch.batch_no || batch.batch_name,
                expiry_date: batch.expiry_date,
              });
              remainingToAllocate -= take;
            }
          }
        }

        const subItemRate = StockTransferManager.getStockTransferItemUnitRate(prod, profitPercentage, rateCalculationMode);
        const subItemTaxPercent = prod?.tax || 0;
        const subItemCessPercent = prod?.cess || 0;

        const tempSubItem = {
          quantity: requiredQty,
          rate: subItemRate,
          tax: subItemTaxPercent,
          cess: subItemCessPercent,
        };

        const subItemCess = StockTransferManager.getItemCess(tempSubItem);
        const subItemTax = StockTransferManager.getItemTax(tempSubItem);

        return {
          id: subItem.id, // ID from details
          item_id: subItem.item_id,
          name: subItem.name || prod?.name || '-',
          quantity: requiredQty,
          fraction: subItem.fraction,
          cost: prod?.cost || 0,
          totalCost: requiredQty * (prod?.cost || 0),
          is_batch_enabled: prod?.is_batch_enabled,
          batch_data: selectedBatches,
          rate: subItemRate,
          tax: subItemTax,
          cess: subItemCess,
          payable: subItemRate * requiredQty + subItemTax + subItemCess,
          plu: prod?.plu || '',
          price: prod?.price || 0,
          tax_percent: subItemTaxPercent,
          cess_percent: subItemCessPercent,
        };
      });
    }

    const data = {
      item_id: item ? item.id : null,
      plu: item ? item.plu : null,
      rate,
      quantity,
      tax: tax === '' ? 0 : tax ?? 0,
      cess: cess === '' ? 0 : cess ?? 0,
      payable: totalAmount,
      name: item ? item.name : '',
      item,
      cost: item ? item.cost : 0,
      batch_data: selectedItemBatches,
      sub_item,
    };

    setTransferItems([...transferItems, data]);
    resetItemFields();
  };

  const getTerminals = async () => {
    const resp = await TerminalService.getTerminals();
    setTerminals(resp);
    const defaultTerminal = resp.find((t) => t.auth_token === null);
    setSelectedTerminal(defaultTerminal);
  };

  const getChainShops = async () => {
    const resp = await StockTransferService.getChainShops();
    setChainShops(resp);
    setSelectedReceivingShop(resp ? resp[0] : null);
  };

  const fetchInitialData = async () => {
    if (unmountTimeout) {
      clearTimeout(unmountTimeout);
      unmountTimeout = null;
    }
    if (initialFetchPromise) {
      await initialFetchPromise;
      return;
    }

    initialFetchPromise = (async () => {
      setLoadingIndicator(true);
      try {
        const promises = [getTerminals(), getProducts(), getChainShops(), getStockBatches()];
        if (ShopsHelper.isWarehouseFeatureActive(shop)) {
          promises.push(getWarehouses({ useCache: true }));
          promises.push(getWarehouseProducts({ useCache: true }));
        }
        await Promise.all(promises);
      } finally {
        setLoadingIndicator(false);
      }
    })();

    await initialFetchPromise;
  };

  const getInvalidComboItem = () => {
    if (transferItems.length === 0) return null;

    const invalidItem = transferItems.find(
      (transferItem) =>
        transferItem.item?.type === 'COMBO' &&
        (transferItem.sub_item || []).some((si) => {
          if (!si.is_batch_enabled) return false;

          const totalSelected = (si.batch_data || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
          return Math.abs(si.quantity - totalSelected) >= 0.001;
        })
    );
    return invalidItem;
  };

  const handleupdateStockTransfer = async (status) => {
    if (!selectedReceivingShop) {
      postAlertMessage({ text: tCommon('Please select a receiving shop'), type: 'failed' });
      return;
    }
    if (transferItems.length === 0) {
      postAlertMessage({ text: tOperations('Please add at least one item'), type: 'failed' });
      return;
    }
    if (item) {
      postAlertMessage({
        text: tOperations('Please add the selected item to the list before saving'),
        type: 'failed',
      });
      return;
    }

    const invalidCombo = getInvalidComboItem();
    if (invalidCombo) {
      postAlertMessage({
        text: `Total batch quantity must match the required quantity for sub items in "${invalidCombo.name}".`,
        type: 'failed',
      });
      return;
    }

    setLoadingIndicator(true);
    try {
      const data = {
        ...editData,
        data: {
          items: transferItems,
          payable: transferTotal?.payable,
          tax: transferTotal?.tax,
          cess: transferTotal?.cess,
          remark,
          terminal: selectedTerminal?.id || null,
          batch_name: batchName,
          rate_calculation_mode: rateCalculationMode,
          customer_id: selectedReceivingShop?.id || null,
        },
        to_shop: selectedReceivingShop?.id || null,
        profit_percentage: profitPercentage,
        status,
        warehouse_id: warehouseId === 0 ? null : warehouseId,
      };

      await StockTransferService.updateStockTransfer(transferId, data);
      await getProducts({ useCache: false });
      const hasBatchItems = transferItems.some(
        (item) =>
          (item.batch_data && item.batch_data.length > 0) ||
          (item.item?.type === 'COMBO' && (item.sub_item || []).some((si) => si.batch_data && si.batch_data.length > 0))
      );
      if (hasBatchItems) {
        await getStockBatches({ useCache: false });
      }
       if (ShopsHelper.isWarehouseFeatureActive(shop)) {
        await getWarehouseProducts({ useCache: false });
      }
      backToTransferHistory();
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleAddStockTransfer = async (status) => {
    if (!selectedReceivingShop) {
      postAlertMessage({ text: tCommon('Please select a receiving shop'), type: 'failed' });
      return;
    }
    if (transferItems.length === 0) {
      postAlertMessage({ text: tOperations('Please add at least one item'), type: 'failed' });
      return;
    }
    if (item) {
      postAlertMessage({
        text: tOperations('Please add the selected item to the list before saving'),
        type: 'failed',
      });
      return;
    }

    const invalidCombo = getInvalidComboItem();
    if (invalidCombo) {
      postAlertMessage({
        text: `Total batch quantity must match the required quantity for sub items in "${invalidCombo.name}".`,
        type: 'failed',
      });
      return;
    }

    setLoadingIndicator(true);

    try {
      const transferData = {
        ...editData,
        data: {
          items: transferItems,
          payable: transferTotal?.payable,
          tax: transferTotal?.tax,
          cess: transferTotal?.cess,
          remark,
          terminal: selectedTerminal?.id || null,
          batch_name: batchName,
          rate_calculation_mode: rateCalculationMode,
        },
        to_shop: selectedReceivingShop?.id || null,
        profit_percentage: profitPercentage,
        status,
        warehouse_id: warehouseId === 0 ? null : warehouseId,
      };

      await StockTransferService.createStockTransfer(transferData);
      await getProducts({ useCache: false });
      const hasBatchItems = transferItems.some(
        (item) =>
          (item.stock_batch && item.stock_batch.length > 0) ||
          (item.batch_data && item.batch_data.length > 0) ||
          (item.item?.type === 'COMBO' && (item.sub_item || []).some((si) => si.batch_data && si.batch_data.length > 0))
      );
      if (hasBatchItems) {
        await getStockBatches({ useCache: false });
      }
       if (ShopsHelper.isWarehouseFeatureActive(shop)) {
        await getWarehouseProducts({ useCache: false });
      }
      postAlertMessage({
        text: tPopup('Stock transfered successfully'),
        type: 'success',
      });
      backToTransferHistory();
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  const getItemAvailableStock = (productItem) => {
    if (!productItem || !productItem.id) return 0;
    const currentWhId = warehouseId === 0 ? null : warehouseId;
    if (currentWhId && Array.isArray(warehouseProducts)) {
      const wp = warehouseProducts.find((p) => {
        const bWhId = p.warehouse?.id || p.warehouse || null;
        const bItemId = p.item?.id || p.item || null;
        return String(bWhId) === String(currentWhId) && String(bItemId) === String(productItem.id);
      });
      return wp ? wp.stock : 0;
    }
    return productItem.stock || 0;
  };

  const selectItemHandler = (item, mode = rateCalculationMode, profit = profitPercentage) => {
    const initialStock = getItemAvailableStock(item);
    let defaultQty = item.is_batch_enabled ? 0 : 1;
    if (shop?.stock_availability_check_for_sale) {
      defaultQty = Math.max(0, Math.min(defaultQty, initialStock));
    }

    const calculatedRate = StockTransferManager.getStockTransferItemUnitRate(item, profit, mode);

    const selectedItem = {
      ...item,
      quantity: defaultQty,
      cost: item.cost,
      rate: calculatedRate,
    };
    setItem(selectedItem || '');
    setItemName(item.plu ? `${item.plu} - ${item.name}` : item.name || '');

    setStock(initialStock ?? 0);
    setRate(calculatedRate ?? 0);
    setQuantity(defaultQty);
    let itemTax = StockTransferManager.getItemTax(selectedItem);
    let itemCess = StockTransferManager.getItemCess(selectedItem);
    setTax(itemTax);
    setCess(itemCess);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, CartManager.getItemAmount(selectedItem) + itemTax + itemCess));
    setSelectedItemBatches([]);
  };

  const handleRateCalculationModeChange = (newMode) => {
    setRateCalculationMode(newMode);
    let effectiveProfit = profitPercentage;
    if (newMode === 'price') {
      effectiveProfit = 0;
      setProfitPercentage(0);
    }
    if (item) {
      selectItemHandler(item, newMode, effectiveProfit);
    }
  };

  const handleChangeRate = (val) => {
    if (val === '') {
      setRate('');
      setTax(0);
      setCess(0);
      setTotalAmount(0);
      return;
    }
    const newRate = Number(val);
    setRate(newRate);
    if (item) {
      const updatedItem = { ...item, rate: newRate, quantity };
      let itemTax = StockTransferManager.getItemTax(updatedItem);
      let itemCess = StockTransferManager.getItemCess(updatedItem);
      setTax(itemTax);
      setCess(itemCess);
      setTotalAmount(ShopsHelper.getAmountFormatted(shop, CartManager.getItemAmount(updatedItem) + itemTax + itemCess));
    } else {
      if (quantity) {
        setTotalAmount(ShopsHelper.getAmountFormatted(shop, newRate * quantity));
      }
    }
  };

  const handleItemQtyChange = (index, val) => {
    setTransferItems((prev) =>
      prev.map((saleItem, i) => {
        if (i !== index) return saleItem;

        let qty = val === '' ? '' : Number(val);
        const numericQty = qty === '' ? 0 : Number(qty);
        if (numericQty < 0) qty = 0;

        if (shop?.stock_availability_check_for_sale && numericQty > 0) {
          const availableStock = getItemAvailableStock(saleItem.item);
          if (numericQty > availableStock) {
            postAlertMessage({
              text: tOperations('Insufficient stock. Available stock is {{stock}}', { stock: availableStock }),
              type: 'failed',
            });
            qty = availableStock;
          }
        }

        let updatedSubItems = saleItem.sub_item || [];
        if (saleItem.item?.type === 'COMBO' && updatedSubItems.length > 0) {
          const currentQty = qty === '' ? 0 : Number(qty);
          updatedSubItems = updatedSubItems.map((subItem) => {
            const requiredQty = subItem.fraction * currentQty;
            const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
            const subItemRate = StockTransferManager.getStockTransferItemUnitRate(
              prod || subItem,
              profitPercentage,
              rateCalculationMode
            );
            const tempSubItem = {
              quantity: requiredQty,
              rate: subItemRate,
              tax_percent: subItem.tax_percent ?? prod?.tax ?? 0,
              cess_percent: subItem.cess_percent ?? prod?.cess ?? 0,
            };
            const subItemCess = StockTransferManager.getItemCess(tempSubItem);
            const subItemTax = StockTransferManager.getItemTax(tempSubItem);
            return {
              ...subItem,
              quantity: requiredQty,
              totalCost: requiredQty * (subItem.cost || 0),
              tax: subItemTax,
              cess: subItemCess,
              payable: subItemRate * requiredQty + subItemTax + subItemCess,
            };
          });
        }

        const itemForTax = {
          ...saleItem,
          quantity: qty === '' ? 0 : Number(qty),
          tax_percent: saleItem.item?.tax ?? saleItem.tax_percent ?? 0,
          cess_percent: saleItem.item?.cess ?? saleItem.cess_percent ?? 0,
        };

        const itemTax = StockTransferManager.getItemTax(itemForTax);
        const itemCess = StockTransferManager.getItemCess(itemForTax);

        const updatedItem = {
          ...saleItem,
          quantity: qty,
          sub_item: updatedSubItems,
          tax: itemTax,
          cess: itemCess,
          payable: CartManager.getItemAmount(itemForTax) + itemTax + itemCess,
        };

        return updatedItem;
      })
    );
  };

  const handleItemRateChange = (index, val) => {
    setTransferItems((prev) =>
      prev.map((saleItem, i) => {
        if (i !== index) return saleItem;

        const newRate = val === '' ? '' : Number(val);
        const numericRate = newRate === '' ? 0 : Number(newRate);

        const itemForTax = {
          ...saleItem,
          rate: numericRate,
          tax_percent: saleItem.item?.tax ?? saleItem.tax_percent ?? 0,
          cess_percent: saleItem.item?.cess ?? saleItem.cess_percent ?? 0,
        };

        const itemTax = StockTransferManager.getItemTax(itemForTax);
        const itemCess = StockTransferManager.getItemCess(itemForTax);

        const updatedItem = {
          ...saleItem,
          rate: newRate,
          tax: itemTax,
          cess: itemCess,
          payable: numericRate * (saleItem.quantity || 0) + itemTax + itemCess,
        };

        return updatedItem;
      })
    );
  };

  const handleBatchQuantityChange = (batchId, val) => {
    let qty = Number(val);
    const batch = (localBatches || []).find((b) => b.id === batchId);
    const adjustedStock = (batch?.stock || 0) + (originalBatchQtys[String(batchId)] || 0);

    if (batch && qty > adjustedStock) {
      qty = adjustedStock;
      postAlertMessage({
        text: `${tOperations('Quantity cannot exceed available stock')} (${adjustedStock})`,
        type: 'failed',
      });
    }

    const updateBatchList = (prev, bId, bQty) => {
      if (bQty <= 0) {
        return prev.filter((b) => b.batch_id !== bId);
      }
      const existing = prev.find((b) => b.batch_id === bId);
      if (existing) {
        return prev.map((b) => (b.batch_id === bId ? { ...b, quantity: bQty } : b));
      } else {
        return [...prev, { batch_id: bId, quantity: bQty, expiry_date: batch?.expiry_date }];
      }
    };

    if (editingTableItemIndex !== null) {
      setTransferItems((prev) =>
        prev.map((si, idx) => {
          if (idx === editingTableItemIndex) {
            const updatedBatches = updateBatchList(si.batch_data || [], batchId, qty);
            const totalQty = updatedBatches.reduce((sum, b) => sum + b.quantity, 0);
            const updatedItem = {
              ...si,
              batch_data: updatedBatches,
              quantity: totalQty,
              tax: si.item?.tax || 0,
              cess: si.item?.cess || 0,
            };
            updatedItem.tax = StockTransferManager.getItemTax(updatedItem);
            updatedItem.cess = StockTransferManager.getItemCess(updatedItem);
            updatedItem.payable = CartManager.getItemAmount(updatedItem) + updatedItem.tax + updatedItem.cess;
            return updatedItem;
          }
          return si;
        })
      );
    } else {
      setSelectedItemBatches((prev) => {
        const newBatches = updateBatchList(prev, batchId, qty);
        const totalBatchQty = newBatches.reduce((sum, b) => sum + b.quantity, 0);
        handleChangeQty(totalBatchQty);
        return newBatches;
      });
    }
  };

  const handleOpenSubItemBatchPopup = (event, saleItemIndex, subItem) => {
    const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
    setSubItemBatchPopup({
      open: true,
      anchorEl: event.currentTarget,
      item: prod,
      subItem: subItem,
      saleItemIndex: saleItemIndex,
    });
  };

  const handleSubItemBatchChange = (batchId, val) => {
    const { saleItemIndex, subItem } = subItemBatchPopup;
    const qtyValue = parseFloat(val) || 0;

    const batch = (localBatches || []).find((b) => String(b.id) === String(batchId));
    const origQty = originalBatchQtys[String(batchId)] || 0;
    const availableStock = (batch?.stock || 0) + origQty;

    let finalQty = qtyValue;
    if (finalQty > availableStock) {
      postAlertMessage({
        text: `${tOperations('Quantity cannot exceed available stock')} (${availableStock})`,
        type: 'failed',
      });
      finalQty = availableStock;
    }

    setTransferItems((prev) => {
      const next = [...prev];
      const targetSaleItem = { ...next[saleItemIndex] };
      const nextSubItemArray = (targetSaleItem.sub_item || []).map((si) => ({ ...si }));

      const subItemIndex = nextSubItemArray.findIndex((si) => si.item_id === subItem.item_id);
      if (subItemIndex === -1) return prev;

      const updatedSubItem = { ...nextSubItemArray[subItemIndex] };
      const newBatches = [...(updatedSubItem.batch_data || [])];
      const batchIndex = newBatches.findIndex((b) => String(b.batch_id) === String(batchId));

      if (batchIndex > -1) {
        if (finalQty > 0) {
          newBatches[batchIndex] = { ...newBatches[batchIndex], quantity: finalQty };
        } else {
          newBatches.splice(batchIndex, 1);
        }
      } else if (finalQty > 0) {
        newBatches.push({
          batch_id: batchId,
          quantity: finalQty,
          batch_no: batch?.batch_no || batch?.batch_name || '-',
          expiry_date: batch?.expiry_date,
        });
      }

      updatedSubItem.batch_data = newBatches;
      nextSubItemArray[subItemIndex] = updatedSubItem;
      targetSaleItem.sub_item = nextSubItemArray;
      next[saleItemIndex] = targetSaleItem;

      setSubItemBatchPopup((prevPopup) => ({
        ...prevPopup,
        subItem: updatedSubItem,
      }));

      return next;
    });
  };

  const getItemRate = useCallback(
    (iRate = rate, iQty = quantity) => {
      return ShopsHelper.getAmountFormatted(shop, Number(iRate) * iQty);
    },
    [rate, quantity, shop]
  );

  const getTax = useCallback(
    (iRate = rate, iQty = quantity, iTax = item?.tax ?? 0) => {
      return ShopsHelper.getAmountFormatted(shop, (getItemRate(iRate, iQty) * iTax) / 100);
    },
    [item?.tax, getItemRate, quantity, rate, shop]
  );

  const getCess = useCallback(
    (iRate = rate, iQty = quantity, iCess = item?.cess ?? 0) => {
      return ShopsHelper.getAmountFormatted(shop, (getItemRate(Number(iRate), iQty) * iCess) / 100);
    },
    [item?.cess, getItemRate, quantity, rate, shop]
  );

  const handleChangeQty = (val) => {
    if (val === '') {
      setQuantity('');
      return;
    }
    let qty = Number(val);
    if (qty < 0) qty = 0;

    if (shop?.stock_availability_check_for_sale) {
      const availableStock = getItemAvailableStock(item);
      const limitStock = Math.max(0, availableStock);
      if (qty > limitStock) {
        postAlertMessage({
          text: tOperations('Insufficient stock. Available stock is {{stock}}', { stock: limitStock }),
          type: 'failed',
        });
        qty = limitStock;
      }
    }

    if (item === null) {
      setQuantity(qty);
      if (rate) {
        setTotalAmount(rate * qty);
      } else if (totalAmount) {
        setRate(totalAmount / qty);
      }
      return;
    }
    let itemTax = getTax(rate, qty);
    let itemCess = getCess(rate, qty);
    setTax(itemTax);
    setCess(itemCess);
    setQuantity(qty);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, getItemRate(rate, qty) + itemTax + itemCess));
  };

  useEffect(() => {
    if (transferItems.length > 0) {
      const updatedItems = CartManager.updateItemRateWithProfit([...transferItems], profitPercentage, rateCalculationMode);
      setTransferItems(updatedItems);
    }

    //eslint-disable-next-line
  }, [profitPercentage, rateCalculationMode]);

  const transferTotal = transferItems.reduce(
    (acc, item) => {
      acc.payable += Number(item.payable) || 0;
      acc.tax += Number(item.tax) || 0;
      acc.cess += Number(item.cess) || 0;
      return acc;
    },
    { payable: 0, tax: 0, cess: 0 }
  );

  useEffect(() => {
    fetchInitialData();
    return () => {
      unmountTimeout = setTimeout(() => {
        initialFetchPromise = null;
      }, 100);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (warehouses && warehouses.length > 0) {
      if (ShopsHelper.isWarehouseFeatureActive(shop)) {
        if (!transferId) {
          const defaultWhId = selectedTerminal?.default_warehouse || shop?.config?.default_warehouse;
          const defaultWh = warehouses.find((w) => w.id === defaultWhId);
          setWarehouseId(defaultWh ? defaultWh.id : warehouses[0].id);
        }
      }
    }
  }, [shop, selectedTerminal, transferId, warehouses]);

  useEffect(() => {
    if (!transferId || chainShops.length === 0 || terminals.length === 0) {
      return;
    }

    const getStockTransfer = async () => {
      const resp = await StockTransferService.getStockTransfer(transferId);
      setEditData(resp);
      const processedItems = (resp?.data?.items || []).map((si) => {
        let updatedItem = si?.item;
        if (updatedItem?.id) {
          const invItem = (products || []).find((p) => p.id === updatedItem.id);
          if (invItem) {
            updatedItem = invItem;
          }
        }

        let sub_item = [];
        if (updatedItem?.type === 'COMBO') {
          // Parse sub_item_batch_data from backend
          let savedSubItemBatches = si.sub_item_batch_data || si.config?.sub_item_batch_data || [];
          if (savedSubItemBatches.length === 0) {
            const nestedSubItems = si.sub_item || si.config?.sub_item || [];
            nestedSubItems.forEach((subItemEntry) => {
              if (subItemEntry.batch_data) {
                subItemEntry.batch_data.forEach((b) => {
                  savedSubItemBatches.push({
                    item_id: subItemEntry.item_id,
                    batch_id: b.batch_id,
                    quantity: b.quantity,
                  });
                });
              }
            });
          }


          sub_item = (updatedItem.details?.sub_items || []).map((subItem) => {
            const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
            const requiredQty = subItem.fraction * si.quantity;

            // Load selected batches for this subItem from saved data
            const selectedBatches = savedSubItemBatches
              .filter((sb) => String(sb.item_id) === String(subItem.item_id))
              .map((sb) => {
                const batch =
                  (localBatches || []).find((b) => String(b.id) === String(sb.batch_id)) ||
                  (stockBatches || []).find((b) => String(b.id) === String(sb.batch_id));
                return {
                  batch_id: sb.batch_id,
                  quantity: sb.quantity,
                  batch_no: batch?.batch_no || batch?.batch_name || '-',
                };
              });

            const subItemRate = StockTransferManager.getStockTransferItemUnitRate(prod, profitPercentage, rateCalculationMode);
            const subItemTaxPercent = prod?.tax || 0;
            const subItemCessPercent = prod?.cess || 0;

            const tempSubItem = {
              quantity: requiredQty,
              rate: subItemRate,
              tax: subItemTaxPercent,
              cess: subItemCessPercent,
            };

            const subItemCess = StockTransferManager.getItemCess(tempSubItem);
            const subItemTax = StockTransferManager.getItemTax(tempSubItem);

            return {
              id: subItem.id,
              item_id: subItem.item_id,
              name: subItem.name || prod?.name || '-',
              quantity: requiredQty,
              fraction: subItem.fraction,
              cost: prod?.cost || 0,
              totalCost: requiredQty * (prod?.cost || 0),
              is_batch_enabled: prod?.is_batch_enabled,
              batch_data: selectedBatches,
              rate: subItemRate,
              tax: subItemTax,
              cess: subItemCess,
              payable: subItemRate * requiredQty + subItemTax + subItemCess,
              plu: prod?.plu || '',
              price: prod?.price || 0,
              tax_percent: subItemTaxPercent,
              cess_percent: subItemCessPercent,
            };
          });
        }

        return {
          ...si,
          item: updatedItem,
          item_id: updatedItem?.id,
          name: updatedItem?.name,
          plu: updatedItem?.plu || si.plu || '',
          batch_data: si.config?.batch_data || si.batch_data || [],
          sub_item,
        };
      });

      setTransferItems(processedItems);
      setProfitPercentage(resp?.profit_percentage || 0);
      if (resp?.data?.rate_calculation_mode) {
        setRateCalculationMode(resp.data.rate_calculation_mode);
      }
      setSelectedTerminal(terminals.find((t) => t.id === resp?.data?.terminal) || null);
      setSelectedReceivingShop(chainShops.find((s) => s.id === resp?.to_shop?.id) || null);
      setRemark(resp?.data?.remark);
      setBatchName(resp?.data?.batch_name || '');

      if (resp?.warehouse !== undefined && resp?.warehouse !== null) {
        setWarehouseId(typeof resp.warehouse === 'object' ? resp.warehouse.id : resp.warehouse);
      } else if (resp?.warehouse_id !== undefined && resp?.warehouse_id !== null) {
        setWarehouseId(resp.warehouse_id);
      }

      // Fetch missing batches
      const missingBatchIds = [];
      processedItems.forEach((item) => {
        if (item.batch_data) {
          item.batch_data.forEach((sb) => {
            if (!localBatches.some((lb) => String(lb.id) === String(sb.batch_id))) {
              missingBatchIds.push(sb.batch_id);
            }
          });
        }
        if (item.sub_item) {
          item.sub_item.forEach((si) => {
            if (si.batch_data) {
              si.batch_data.forEach((sb) => {
                if (!localBatches.some((lb) => String(lb.id) === String(sb.batch_id))) {
                  missingBatchIds.push(sb.batch_id);
                }
              });
            }
          });
        }
      });

      if (missingBatchIds.length > 0) {
        try {
          const fetchedBatches = await Promise.all(missingBatchIds.map((id) => ItemService.getStockBatch(id)));
          setLocalBatches((prev) => {
            const existingIds = prev.map((b) => String(b.id));
            const uniqueNew = fetchedBatches.filter((b) => b && !existingIds.includes(String(b.id)));
            return [...prev, ...uniqueNew];
          });
        } catch (err) {
          console.error('Failed to fetch missing batches:', err);
        }
      }

      // Populate originalBatchQtys
      const origQtys = {};
      processedItems.forEach((item) => {
        (item.batch_data || []).forEach((sb) => {
          origQtys[String(sb.batch_id)] = (origQtys[String(sb.batch_id)] || 0) + (Number(sb.quantity) || 0);
        });
        if (item.sub_item) {
          item.sub_item.forEach((si) => {
            (si.batch_data || []).forEach((sb) => {
              origQtys[String(sb.batch_id)] = (origQtys[String(sb.batch_id)] || 0) + (Number(sb.quantity) || 0);
            });
          });
        }
      });
      setOriginalBatchQtys(origQtys);
    };
    getStockTransfer();
    //eslint-disable-next-line
  }, [transferId, terminals, chainShops]);

  useEffect(() => {
    if (stockBatches && stockBatches.length > 0) {
      setLocalBatches((prev) => {
        const stockBatchesMap = new Map(stockBatches.map((b) => [String(b.id), b]));
        const updatedPrev = prev.map((b) => {
          const key = String(b.id);
          if (stockBatchesMap.has(key)) {
            return stockBatchesMap.get(key);
          }
          return b;
        });
        const prevIds = new Set(prev.map((b) => String(b.id)));
        const newBatches = stockBatches.filter((b) => !prevIds.has(String(b.id)));
        return [...updatedPrev, ...newBatches];
      });
    }
  }, [stockBatches]);

  const handleChangeTerminal = (event) => {
    const selectedId = event.target.value;
    const selected = terminals.find((t) => t.id === selectedId) || null;

    if (selected && selected.auth_token) {
      setDisabledTerminal(selected);
      return;
    }

    setSelectedTerminal(selected);
  };

  const backToTransferHistory = () => {
    history.push(ROUTES.STOCK_TRANSFER);
  };

  return (
    <>
      <div className={styles.contentWrapper}>
        <div className={styles.titleSec}>
          <h2 className={styles.title}>
            {tCommon('Stock Transfer')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
          </h2>
        </div>
        <div className={styles.changeable}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <h2 className={styles.subTitle}>
                {editData.id ? tCommon('Edit Stock Transfer') : tCommon('New Stock Transfer')}
              </h2>
            </Grid>
          </Grid>
          <Grid container justify="flex-end" alignItems="flex-start" pt={2} rowSpacing={2} spacing={2}>
            <Grid item xs={12} sm={6} pt={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="terminal-select-label">{tCommon('Receiving Shop')}</InputLabel>
                <Select
                  size="small"
                  labelId="terminal-select-label"
                  value={selectedReceivingShop?.id ?? ''}
                  onChange={(e) => {
                    const shop = chainShops.find((s) => s.id === e.target.value) || null;
                    setSelectedReceivingShop(shop);
                  }}
                  label={tCommon('Receiving Shop')}
                  style={{
                    height: 42,
                  }}
                  renderValue={(value) => {
                    const selected = chainShops.find((s) => s.id === value);
                    return selected?.shop_name;
                  }}
                  disabled={transferId}
                >
                  {chainShops.map((shop) => (
                    <MenuItem key={shop.id} value={shop.id} disabled={false}>
                      {shop.shop_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} pt={2} pl={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="terminal-select-label">{tCommon('Terminal')}</InputLabel>
                <Select
                  size="small"
                  labelId="terminal-select-label"
                  value={selectedTerminal?.id ?? ''}
                  onChange={handleChangeTerminal}
                  label={tCommon('Terminal')}
                  style={{
                    height: 42,
                  }}
                  renderValue={(value) => {
                    if (value === null) return tCommon('None');
                    const selected = terminals.find((t) => t.id === value);
                    return selected?.name || tCommon('None');
                  }}
                  disabled={transferId}
                >
                  <MenuItem value={null}>
                    <Typography>{tCommon('None')}</Typography>
                  </MenuItem>
                  {terminals.map((terminal) => (
                    <MenuItem
                      key={terminal.id}
                      value={terminal.id}
                      disabled={false}
                      style={{
                        color: terminal.auth_token ? 'gray' : 'inherit',
                        opacity: terminal.auth_token ? 0.6 : 1,
                      }}
                    >
                      {terminal.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {ShopsHelper.isWarehouseFeatureActive(shop) && (
              <Grid item xs={12} sm={6} pt={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="warehouse-select-label">{tCommon('Warehouse')}</InputLabel>
                  <Select
                    size="small"
                    labelId="warehouse-select-label"
                    value={warehouseId || ''}
                    onChange={(e) => {
                      const newWarehouseId = e.target.value === '' ? null : Number(e.target.value);
                      if (newWarehouseId !== warehouseId) {
                        setWarehouseId(newWarehouseId);
                        resetItemFields();
                        setTransferItems([]);
                        setOriginalBatchQtys({});
                      }
                    }}
                    label={tCommon('Warehouse')}
                    style={{
                      height: 42,
                    }}
                    disabled={transferId}
                  >
                    <MenuItem value="">
                      <em>{tCommon('None')}</em>
                    </MenuItem>
                    {(warehouses || []).map((wh) => (
                      <MenuItem key={wh.id} value={wh.id}>
                        {wh.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} pt={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="rate-calc-mode-label">{tCommon('Calculate Price From')}</InputLabel>
                <Select
                  size="small"
                  labelId="rate-calc-mode-label"
                  value={rateCalculationMode}
                  onChange={(e) => handleRateCalculationModeChange(e.target.value)}
                  label={tCommon('Calculate Price From')}
                  style={{
                    height: 42,
                  }}
                >
                  <MenuItem value="cost">{tCommon('Cost')}</MenuItem>
                  <MenuItem value="price">{tCommon('Price')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {rateCalculationMode === 'cost' && (
              <Grid item xs={12} sm={6} pt={2}>
                <TextField
                  label={tCommon('Profit Percentage')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={profitPercentage}
                  onChange={(e) => setProfitPercentage(Number(e.target.value))}
                  type="text"
                  disabled={Boolean(item)}
                />
              </Grid>
            )}
            {hasBatchEnabledItems && (
              <Grid item xs={12} sm={6} pt={2}>
                <TextField
                  label={tCommon('Batch Name')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </div>
        <div className={styles.changeable} style={{ boxShadow: 'none', padding: '0', overflowX: 'auto' }}>
          <TableRow key="input row">
            <TableCell>
              <Autocomplete
                value={item}
                onChange={(event, newValue) => {
                  if (!newValue) {
                    resetItemFields(null);
                  } else {
                    selectItemHandler(newValue || null);
                  }
                }}
                inputValue={itemName}
                onInputChange={(e, value) => {
                  setItemName(value);
                }}
                getOptionLabel={(option) => option ? (option.plu ? `${option.plu} - ${option.name}` : option.name || '') : ''}
                options={filteredProductsList || []}
                ListboxProps={{
                  sx: {
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tCommon('Product')}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <MenuItem
                      key={key}
                      {...optionProps}
                      style={{
                        paddingTop: '1px',
                        paddingBottom: '1px',
                      }}
                    >
                      {option.name}
                    </MenuItem>
                  );
                }}
                freeSolo={false}
                style={{ minWidth: '250px' }}
              />
            </TableCell>
            <TableCell>
              <TextField
                label={tCommon('Stock')}
                variant="outlined"
                size="small"
                fullWidth
                value={stock}
                disabled
                type="text"
                style={{ minWidth: '30px' }}
              />
            </TableCell>
            <TableCell>
              <NumberField
                label={tCommon('Rate')}
                value={rate}
                onChange={(e) => handleChangeRate(e.target.value)}
                style={{ minWidth: '60px' }}
                disabled={!item}
              />
            </TableCell>
            <TableCell>
              {item?.is_batch_enabled ? (
                <NumberField
                  type="text"
                  label={tCommon('Qty')}
                  value={quantity}
                  onChange={(e) => handleChangeQty(e.target.value)}
                  style={{ minWidth: '100px', cursor: 'pointer' }}
                  sx={{ '& input': { cursor: 'pointer' } }}
                  inputProps={{ readOnly: true }}
                  onKeyDown={(e) => e.preventDefault()}
                  InputProps={{
                    onClick: (e) => {
                      setBatchPopupAnchor(e.currentTarget);
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <MuiIconButton color="primary" size="small">
                          <ArrowDropDownIcon fontSize="small" />
                        </MuiIconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <NumberField
                  label={tCommon('Qty')}
                  value={quantity}
                  onChange={(e) => handleChangeQty(e.target.value)}
                  style={{ minWidth: '40px' }}
                />
              )}
            </TableCell>
            <TableCell>
              <NumberField
                label={tCommon('Tax')}
                disabled={item}
                value={Number(tax) + Number(cess) || 0}
                style={{ minWidth: '60px' }}
              />
            </TableCell>
            <TableCell>
              <NumberField label={tCommon('Payable')} value={totalAmount} style={{ minWidth: '60px' }} disabled />
            </TableCell>
            <TableCell>
              <IconButton disabled={quantity === 0 || rate === ''} onClick={addItem}>
                <LibraryAddIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        </div>

        <TableContainer style={{ marginTop: 4 }} component={Paper}>
          <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                <TableCell style={{ minWidth: '80px' }} className={styles.columnCell}>
                  {tCommon('PLU')}
                </TableCell>
                <TableCell style={{ minWidth: '200px' }} className={styles.nameColumn}>
                  {tCommon('Product')}
                </TableCell>
                <TableCell style={{ minWidth: '75px' }} className={styles.columnCell}>
                  {tCommon('Rate')}
                </TableCell>
                <TableCell style={{ minWidth: '65px' }} className={styles.columnCell}>
                  {tCommon('Qty')}
                </TableCell>
                <TableCell style={{ minWidth: '75px' }} className={styles.columnCell}>
                  {tCommon('Total Tax')}
                </TableCell>
                <TableCell style={{ minWidth: '75px' }} className={styles.columnCell}>
                  {tCommon('Total')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(loadingIndicator || products === null) &&
                [...Array(3)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton variant="text" animation="wave" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" animation="wave" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" animation="wave" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" animation="wave" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" animation="wave" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" animation="wave" />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))}
              {transferItems &&
                transferItems.map((saleItem, index) => (
                  <TransferItemRow
                    key={saleItem?.item_id}
                    saleItem={saleItem}
                    index={index}
                    dltItem={dltItem}
                    shop={shop}
                    tCommon={tCommon}
                    tOperations={tOperations}
                    localBatches={filteredStockBatches}
                    originalBatchQtys={originalBatchQtys}
                    handleOpenSubItemBatchPopup={handleOpenSubItemBatchPopup}
                    setEditingTableItemIndex={setEditingTableItemIndex}
                    setBatchPopupAnchor={setBatchPopupAnchor}
                    handleItemQtyChange={handleItemQtyChange}
                    handleItemRateChange={handleItemRateChange}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer style={{ marginTop: 12 }} component={Paper}>
          <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={3} style={{ width: '50%' }}>
                  <TextField
                    id="outlined-multiline-static"
                    label={tOperations('Remark')}
                    multiline
                    rows={6}
                    value={remark}
                    variant="outlined"
                    fullWidth
                    onChange={(event) => setRemark(event.target.value)}
                  />
                </TableCell>
                <TableCell colSpan={2}>{tCommon('Total Amount')}</TableCell>
                <TableCell align="right" style={{ fontWeight: 600 }}>
                  {ShopsHelper.getAmountFormatted(shop, Number(transferTotal?.payable) + Number(totalAmount))}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tCommon('Total Tax')}</TableCell>
                <TableCell colSpan={2} align="right" style={{ fontWeight: 600 }}>
                  {ShopsHelper.getAmountFormatted(
                    shop,
                    Number(transferTotal?.tax) + Number(transferTotal?.cess) + Number(tax) + Number(cess)
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tCommon('Payable')}</TableCell>
                <TableCell colSpan={2} align="right" style={{ fontWeight: 600 }}>
                  {ShopsHelper.getAmountFormatted(shop, Number(transferTotal?.payable) + Number(totalAmount))}
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
        <Grid container className={styles.submitSection} display="flex" justify="flex-end">
          {editData.id ? (
            <Button variant="outlined" color="primary" className={styles.draftBtn} onClick={backToTransferHistory}>
              {tCommon('Back')}
            </Button>
          ) : (
            <Button variant="outlined" color="primary" className={styles.draftBtn} onClick={clearSaleHandler}>
              {tCommon('Cancel')}
            </Button>
          )}

          {editData.id && editData.status === TRANSFER_STATUS.DRAFT && (
            <>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 5 }}
                className={styles.draftBtn}
                disabled={loadingIndicator}
                onClick={() => handleupdateStockTransfer(TRANSFER_STATUS.DRAFT)}
              >
                {tCommon('Update as draft')}
              </Button>
            </>
          )}
          {editData.id && (
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: 5 }}
              disabled={loadingIndicator}
              className={styles.draftBtn}
              onClick={() => handleupdateStockTransfer(TRANSFER_STATUS.TRANSIT)}
            >
              {tCommon('Update as transit')}
            </Button>
          )}

          {!editData.id && (
            <>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 5 }}
                onClick={() => handleAddStockTransfer(TRANSFER_STATUS.DRAFT)}
                disabled={loadingIndicator}
                className={styles.draftBtn}
              >
                {tCommon('Save as Draft')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 5 }}
                onClick={() => handleAddStockTransfer(TRANSFER_STATUS.TRANSIT)}
                disabled={loadingIndicator}
                className={styles.draftBtn}
              >
                {tCommon('Create Transfer')}
              </Button>
            </>
          )}
        </Grid>
      </div>
      <MessagePopup
        data={disabledTerminal}
        handleClose={() => setDisabledTerminal(null)}
        alertTitle={tOperations('Terminal Disabled')}
        alertContent={tOperations(
          'This terminal is currently in use on a POS device. Please select another terminal, log out from the POS to use this terminal, or proceed with creating the sale without selecting a terminal.'
        )}
        successBtnName={tOperations('Okay')}
      />
      <BatchSelectionPopup
        open={subItemBatchPopup.open}
        anchorEl={subItemBatchPopup.anchorEl}
        onClose={() => setSubItemBatchPopup({ ...subItemBatchPopup, open: false })}
        item={subItemBatchPopup.item}
        stockBatches={filteredStockBatches}
        selectedBatches={subItemBatchPopup.subItem?.batch_data || []}
        originalBatchQtys={originalBatchQtys}
        onChange={handleSubItemBatchChange}
        showExpired={false}
      />
      <BatchSelectionPopup
        open={Boolean(batchPopupAnchor)}
        anchorEl={batchPopupAnchor}
        onClose={() => {
          setBatchPopupAnchor(null);
          setEditingTableItemIndex(null);
        }}
        item={editingTableItemIndex !== null ? transferItems[editingTableItemIndex]?.item : item}
        stockBatches={filteredStockBatches}
        selectedBatches={
          editingTableItemIndex !== null
            ? transferItems[editingTableItemIndex]?.batch_data || []
            : selectedItemBatches
        }
        originalBatchQtys={originalBatchQtys}
        onChange={handleBatchQuantityChange}
        showExpired={false}
      />
    </>
  );
}

function TransferItemRow({
  saleItem,
  index,
  dltItem,
  shop,
  tCommon,
  tOperations,
  localBatches,
  originalBatchQtys,
  handleOpenSubItemBatchPopup,
  setEditingTableItemIndex,
  setBatchPopupAnchor,
  handleItemQtyChange,
  handleItemRateChange,
}) {
  const [open, setOpen] = useState(true);

  return (
    <>
      <TableRow>
        <TableCell>{saleItem.item?.plu || saleItem.plu || '-'}</TableCell>
        <TableCell>
          {saleItem.item?.type === 'COMBO' && (
            <IconButton size="small" onClick={() => setOpen(!open)} style={{ marginRight: 4 }}>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          {saleItem?.name || <span className={styles.disableText}>{tOperations('No product')}</span>}
          {saleItem.batch_data && saleItem.batch_data.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {saleItem.batch_data.map((sb, idx) => {
                const batch = (localBatches || []).find((b) => String(b.id) === String(sb.batch_id));
                return (
                  <div key={sb.batch_id}>
                    {batch?.batch_name || batch?.batch_no || sb.batch_id}: {sb.quantity}
                    {idx < saleItem.batch_data.length - 1 ? ', ' : ''}
                  </div>
                );
              })}
            </div>
          )}
        </TableCell>
        <TableCell>
          <NumberField
            value={saleItem.rate === '' ? '' : saleItem.rate}
            onChange={(e) => handleItemRateChange(index, e.target.value)}
            style={{ minWidth: '75px' }}
          />
        </TableCell>
        <TableCell>
          {saleItem?.item?.is_batch_enabled ? (
            <NumberField
              type="text"
              value={saleItem.quantity === 0 ? '' : saleItem.quantity}
              style={{ cursor: 'pointer', minWidth: '65px' }}
              sx={{ '& input': { cursor: 'pointer' } }}
              inputProps={{ readOnly: true }}
              onKeyDown={(e) => e.preventDefault()}
              InputProps={{
                onClick: (e) => {
                  setEditingTableItemIndex(index);
                  setBatchPopupAnchor(e.currentTarget);
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <MuiIconButton color="primary" size="small">
                      <ArrowDropDownIcon fontSize="small" />
                    </MuiIconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <NumberField
              value={saleItem.quantity === '' ? '' : saleItem.quantity}
              onChange={(e) => handleItemQtyChange(index, e.target.value)}
              style={{ minWidth: '65px' }}
            />
          )}
        </TableCell>
        <TableCell>
          <NumberField
            value={ShopsHelper.getAmountFormatted(shop, Number(saleItem.tax) + Number(saleItem.cess) || 0)}
          />
        </TableCell>
        <TableCell>
          <NumberField value={saleItem.payable ? ShopsHelper.getAmountFormatted(shop, saleItem.payable) : '0.00'} />
        </TableCell>
        <TableCell>
          <IconButton onClick={() => dltItem(saleItem)}>
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {saleItem.item?.type === 'COMBO' && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom component="div" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  {tCommon('Sub Items')}
                </Typography>
                <Table size="small" aria-label="sub-items">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600 }}>{tCommon('PLU')}</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>{tCommon('Name')}</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>{tCommon('Required Qty')}</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>{tCommon('Selected Batches')}</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>{tCommon('Remaining Qty')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(saleItem.sub_item || []).map((si) => {
                      const totalSelected = (si.batch_data || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
                      const remaining = Math.max(0, si.quantity - totalSelected);
                      return (
                        <TableRow key={si.item_id}>
                          <TableCell>{si.plu || '-'}</TableCell>
                          <TableCell>
                            {si.name}
                            {si.is_batch_enabled && si.batch_data && si.batch_data.length > 0 && (
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {si.batch_data.map((sb, idx) => {
                                  const batch = (localBatches || []).find((b) => String(b.id) === String(sb.batch_id));
                                  return (
                                    <span key={sb.batch_id}>
                                      {batch?.batch_name || batch?.batch_no || sb.batch_id}: {sb.quantity.toFixed(3)}
                                      {idx < si.batch_data.length - 1 ? ', ' : ''}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{si.quantity.toFixed(3)}</TableCell>
                          <TableCell>
                            {si.is_batch_enabled ? (
                              <NumberField
                                value={totalSelected === 0 ? '' : totalSelected}
                                style={{ minWidth: '100px', cursor: 'pointer' }}
                                sx={{ '& input': { cursor: 'pointer' } }}
                                inputProps={{ readOnly: true }}
                                onKeyDown={(e) => e.preventDefault()}
                                InputProps={{
                                  onClick: (e) => {
                                    handleOpenSubItemBatchPopup(e, index, si);
                                  },
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <MuiIconButton color="primary" size="small">
                                        <ArrowDropDownIcon fontSize="small" />
                                      </MuiIconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell style={{ color: Math.abs(remaining) > 0.001 ? 'red' : 'inherit' }}>
                            {si.is_batch_enabled ? remaining.toFixed(3) : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default withConsoleBase(NewStockTransfer);
